import React, { useEffect, useMemo, useState } from "react";
import {
    ActivityIndicator,
    Modal,
    Pressable,
    RefreshControl,
    ScrollView,
    StyleSheet,
    Text,
    View,
} from "react-native";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import { RouteProp, useRoute } from "@react-navigation/native";
import {
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    endOfYear,
    format,
    parseISO,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    startOfYear,
} from "date-fns";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { ConfirmDialog } from "../../../shared/ui/ConfirmDialog";
import { Button } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";
import { ManglaAartiService } from "../../../services/ManglaAartiService";
import type { ManglaAartiFilterModel, ManglaAartiModel } from "../../../models/ManglaAartiModel";
import { useAuth } from "../../../../context/AuthContext";
import { AuthStackParamList } from "../../../navigation/navigationTypes";
import { resolveHistoryRange } from "../../../shared/utils/historyNavigation";

type FilterType = "Weekly" | "Monthly" | "Quarterly" | "Yearly" | "Custom";
type ManglaAartiHistoryRouteProp = RouteProp<AuthStackParamList, "ManglaAartiHistory">;

export function ManglaAartiHistoryScreen() {
    const route = useRoute<ManglaAartiHistoryRouteProp>();
    const { user } = useAuth();
    const today = useMemo(() => new Date(), []);
    const initialRange = resolveHistoryRange(route.params, today);

    const effectiveUserId = useMemo(() => {
        return initialRange.devoteeId || user?.userId || "";
    }, [initialRange.devoteeId, user?.userId]);

    const [filterType, setFilterType] = useState<FilterType>(
        initialRange.hasNavigationDates ? "Custom" : "Monthly"
    );

    const [summary, setSummary] = useState({ isSelf: true });

    const [fromDate, setFromDate] = useState(initialRange.fromDate);
    const [toDate, setToDate] = useState(initialRange.toDate);

    const [entries, setEntries] = useState<ManglaAartiModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    // edit modal
    const [editing, setEditing] = useState<ManglaAartiModel | null>(null);
    const [editTemple, setEditTemple] = useState(false);
    const [editHomeBefore5, setEditHomeBefore5] = useState(false);
    const [editHomeAfter5, setEditHomeAfter5] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

    useEffect(() => {
        loadHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromDate, toDate]);

    const applyFilter = (type: FilterType) => {
        setFilterType(type);
        if (type === "Custom") return;

        const now = new Date();
        let start = startOfMonth(now);
        let end = endOfMonth(now);

        if (type === "Weekly") {
            start = startOfWeek(now);
            end = endOfWeek(now);
        } else if (type === "Quarterly") {
            start = startOfQuarter(now);
            end = endOfQuarter(now);
        } else if (type === "Yearly") {
            start = startOfYear(now);
            end = endOfYear(now);
        }

        setFromDate(format(start, "yyyy-MM-dd"));
        setToDate(format(end, "yyyy-MM-dd"));
    };

    const openPicker = (mode: "from" | "to") => {
        const value = new Date(mode === "from" ? fromDate : toDate);
        DateTimePickerAndroid.open({
            value,
            mode: "date",
            onChange: (event, selectedDate) => {
                if (event.type !== "set" || !selectedDate) return;
                const v = format(selectedDate, "yyyy-MM-dd");
                if (mode === "from") setFromDate(v);
                else setToDate(v);
                setFilterType("Custom");
            },
        });
    };

    const loadHistory = async () => {
        try {
            
            if (!effectiveUserId) return;
            setLoading(true);

            const payload: ManglaAartiFilterModel = { fromDate, toDate, userId: effectiveUserId };
            const res = await ManglaAartiService.getManglaAartiHistory(payload);

            if (res?.status != 200) {
                Flash.error(res?.message || "Failed to load history");
                setEntries([]);
                return;
            }

            const list = (res.data.records || []) as ManglaAartiModel[];

            // extra safety (if backend already filters, no harm)
            const uid = user?.userId?.toLowerCase();

            const filtered = uid
                ? list.filter((x) => (x.userId ?? "").toLowerCase() === uid)
                : list;

            setEntries(list);
            setSummary(res.data.response);
            setEntries(list);
        } catch (e: any) {
            Flash.error(e?.message || "History load error");
        } finally {
            setLoading(false);
        }
    };

    const calcMarks = (t: boolean, hb: boolean, ha: boolean) => {
        let m = 0;
        if (t) m += ManglaAartiService.MANGLA_AARTI.TEMPLE;
        if (hb) m += ManglaAartiService.MANGLA_AARTI.HOME_BEFORE_5;
        if (ha) m += ManglaAartiService.MANGLA_AARTI.HOME_AFTER_5;
        return m;
    };

    const openEdit = (entry: ManglaAartiModel) => {
        setEditing(entry);
        setEditTemple(!!entry.temple);
        setEditHomeBefore5(!!entry.homeBefore5);
        setEditHomeAfter5(!!entry.homeAfter5);
    };

    const saveEdit = async () => {
        if (!editing) return;

        try {
            if (!user?.userId) {
                Flash.error("User not found");
                return;
            }

            setLoading(true);

            const payload: ManglaAartiModel = {
                manglaAartiId: editing.manglaAartiId ?? null,
                userId: user.userId,
                date: editing.date,
                temple: editTemple,
                homeBefore5: editHomeBefore5,
                homeAfter5: editHomeAfter5,
                marks: calcMarks(editTemple, editHomeBefore5, editHomeAfter5),
                isEditable: false
            };

            const res = await ManglaAartiService.saveManglaAarti(payload);

            if (res?.status != 200) {
                Flash.error(res?.message || "Failed to update");
                return;
            }

            Flash.success("Success!", "Mangla Aarti Updated Successfully!");
            setEditing(null);
            await loadHistory();
        } catch (e: any) {
            Flash.error(e?.message || "Update failed");
        } finally {
            setLoading(false);
        }
    };

    const deleteEntry = (id?: string | null) => {
        if (!id) return;
        setPendingDeleteId(id);
    };

    const onRefresh = async () => {
        setRefreshing(true);
        try {
            await loadHistory();
        } finally {
            setRefreshing(false);
        }
    };

    const Chip = ({ text, active, onPress }: { text: string; active: boolean; onPress: () => void }) => (
        <Pressable
            onPress={onPress}
            style={({ pressed }) => [
                styles.chip,
                active ? styles.chipActive : styles.chipInactive,
                pressed ? { opacity: 0.95 } : null,
            ]}
        >
            <Text style={[styles.chipText, { color: active ? "#fff" : "#B45309" }]}>{text}</Text>
        </Pressable>
    );

    const StatusDot = ({ active }: { active: boolean }) => (
        <View style={[styles.dot, active ? styles.dotActive : styles.dotInactive]}>
            <Text style={{ fontSize: 10, fontWeight: "900", color: active ? "#16A34A" : "rgba(74,35,17,0.25)" }}>
                {active ? "✓" : "–"}
            </Text>
        </View>
    );

    const EditOption = ({ label, checked, onPress }: { label: string; checked: boolean; onPress: () => void }) => (
        <Pressable onPress={onPress} style={[styles.editOpt, checked ? styles.editOptActive : null]}>
            <Text style={styles.editOptText}>{label}</Text>
            <Text style={[styles.editOptTick, { opacity: checked ? 1 : 0.25 }]}>✓</Text>
        </Pressable>
    );

    return (
        <DivineBackground overlayOpacity={0.95}>
            <Header title="History & Analytics" showBack showHome />

            <ScrollView
                contentContainerStyle={styles.page}
                showsVerticalScrollIndicator={false}
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={onRefresh}
                        colors={["#F97316"]}
                        tintColor="#F97316"
                    />
                }
            >
                {/* Filter */}
                <View style={styles.filterCard}>
                    <View style={styles.filterHead}>
                        <Text style={styles.filterIcon}>⛭</Text>
                        <Text style={styles.filterTitle}>Filter History</Text>
                    </View>

                    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingBottom: 8 }}>
                        <Chip text="Weekly" active={filterType === "Weekly"} onPress={() => applyFilter("Weekly")} />
                        <Chip text="Monthly" active={filterType === "Monthly"} onPress={() => applyFilter("Monthly")} />
                        <Chip text="Quarterly" active={filterType === "Quarterly"} onPress={() => applyFilter("Quarterly")} />
                        <Chip text="Yearly" active={filterType === "Yearly"} onPress={() => applyFilter("Yearly")} />
                    </ScrollView>

                    <View style={styles.dateRow}>
                        <View style={{ flex: 1 }}>
                            <Text style={styles.dateLabel}>From Date</Text>
                            <Pressable onPress={() => openPicker("from")} style={styles.dateBox}>
                                <Text style={styles.dateBoxIcon}>📅</Text>
                                <Text style={styles.dateBoxText}>{format(new Date(fromDate), "dd-MM-yyyy")}</Text>
                            </Pressable>
                        </View>

                        <View style={{ flex: 1 }}>
                            <Text style={styles.dateLabel}>To Date</Text>
                            <Pressable onPress={() => openPicker("to")} style={styles.dateBox}>
                                <Text style={styles.dateBoxIcon}>📅</Text>
                                <Text style={styles.dateBoxText}>{format(new Date(toDate), "dd-MM-yyyy")}</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>

                {/* List */}
                {loading && entries.length === 0 ? (
                    <View style={styles.emptyBox}>
                        <ActivityIndicator />
                        <Text style={styles.emptyText}>Loading...</Text>
                    </View>
                ) : entries.length === 0 ? (
                    <View style={styles.emptyBox}>
                        <Text style={styles.emptyText}>No records found for this period.</Text>
                    </View>
                ) : (
                    <View style={{ gap: 10 }}>
                        {entries.map((entry) => (
                            <View key={entry.manglaAartiId || entry.date} style={styles.itemCard}>
                                <View style={styles.itemTop}>
                                    <View>
                                        <Text style={styles.itemDate}>{format(parseISO(entry.date), "dd MMM yyyy")}</Text>
                                        <Text style={styles.itemDay}>{format(parseISO(entry.date), "EEEE")}</Text>
                                    </View>

                                    <View style={{ alignItems: "flex-end" }}>
                                        <Text style={styles.itemMarks}>{entry.marks}</Text>
                                        <Text style={styles.itemMarksLabel}>Marks</Text>
                                    </View>
                                </View>

                                <View style={styles.itemBottom}>
                                    <View style={styles.statusRow}>
                                        <View style={styles.statusCol}>
                                            <StatusDot active={!!entry.temple} />
                                            <Text style={styles.statusText}>Temple</Text>
                                        </View>
                                        <View style={styles.statusCol}>
                                            <StatusDot active={!!entry.homeBefore5} />
                                            <Text style={styles.statusText}>Home&lt;5</Text>
                                        </View>
                                        <View style={styles.statusCol}>
                                            <StatusDot active={!!entry.homeAfter5} />
                                            <Text style={styles.statusText}>Home&gt;5</Text>
                                        </View>
                                    </View>

                                    {summary.isSelf && (
                                        <View style={styles.actionRow}>
                                            {entry.isEditable ? (
                                                <Pressable onPress={() => openEdit(entry)} style={styles.iconBtn} disabled={loading}>
                                                    <Text style={styles.iconBtnText}>✏️</Text>
                                                </Pressable>
                                            ) : (
                                                <View style={[styles.iconBtn, styles.iconBtnPlaceholder]} />
                                            )}
                                            <Pressable onPress={() => deleteEntry(entry.manglaAartiId)} style={[styles.iconBtn, styles.iconBtnDanger]} disabled={loading}>
                                                <Text style={[styles.iconBtnText, { color: "#DC2626" }]}>🗑</Text>
                                            </Pressable>
                                        </View>
                                    )}
                                </View>
                            </View>
                        ))}
                    </View>
                )}
            </ScrollView>

            {/* Edit Modal */}
            <Modal transparent visible={!!editing} animationType="fade" onRequestClose={() => setEditing(null)}>
                <View style={styles.modalOverlay}>
                    <View style={styles.modalCard}>
                        <View style={styles.modalHead}>
                            <Text style={styles.modalTitle}>Edit Entry</Text>
                            <Pressable onPress={() => setEditing(null)} style={styles.modalClose}>
                                <Text style={{ fontSize: 16, fontWeight: "900" }}>✕</Text>
                            </Pressable>
                        </View>

                        {editing ? (
                            <Text style={styles.modalDate}>
                                Date: {format(parseISO(editing.date), "dd MMMM yyyy")}
                            </Text>
                        ) : null}

                        <View style={{ gap: 10, marginTop: 14 }}>
                            <EditOption
                                label={`At Temple (${ManglaAartiService.MANGLA_AARTI.TEMPLE})`}
                                checked={editTemple}
                                onPress={() => setEditTemple((p) => !p)}
                            />
                            <EditOption
                                label={`Home Before 5 AM (${ManglaAartiService.MANGLA_AARTI.HOME_BEFORE_5})`}
                                checked={editHomeBefore5}
                                onPress={() => setEditHomeBefore5((p) => !p)}
                            />
                            <EditOption
                                label={`Home After 5 AM (${ManglaAartiService.MANGLA_AARTI.HOME_AFTER_5})`}
                                checked={editHomeAfter5}
                                onPress={() => setEditHomeAfter5((p) => !p)}
                            />
                        </View>

                        <View style={styles.modalBtns}>
                            <Pressable onPress={() => setEditing(null)} style={styles.modalCancel} disabled={loading}>
                                <Text style={styles.modalCancelText}>Cancel</Text>
                            </Pressable>
                            <View style={{ flex: 1 }}>
                                <Button title={loading ? "Updating..." : "Update"} onPress={saveEdit} disabled={loading} />
                            </View>
                        </View>

                        {loading ? (
                            <View style={{ marginTop: 10, alignItems: "center" }}>
                                <ActivityIndicator />
                            </View>
                        ) : null}
                    </View>
                </View>
            </Modal>
            <ConfirmDialog
                visible={!!pendingDeleteId}
                title="Delete Entry"
                message="Are you sure you want to delete this Mangla Aarti entry?"
                confirmText="Delete"
                destructive
                onCancel={() => setPendingDeleteId(null)}
                onConfirm={async () => {
                    if (!pendingDeleteId) return;
                    try {
                        setLoading(true);
                        const res = await ManglaAartiService.delete(pendingDeleteId);
                        if (res?.status != 200) {
                            Flash.error(res?.message || "Failed to delete");
                            return;
                        }
                        Flash.success("Success!", "Mangla Aarti Deleted Successfully!");
                        setPendingDeleteId(null);
                        await loadHistory();
                    } catch (e: any) {
                        Flash.error(e?.message || "Delete failed");
                    } finally {
                        setLoading(false);
                    }
                }}
            />
        </DivineBackground>
    );
}

const styles = StyleSheet.create({
    page: { padding: 14, paddingBottom: 40, gap: 12 },

    filterCard: {
        backgroundColor: "rgba(255,255,255,0.88)",
        borderRadius: 16,
        padding: 14,
        borderWidth: 1,
        borderColor: "rgba(251,146,60,0.22)",
        shadowColor: "#000",
        shadowOpacity: 0.10,
        shadowRadius: 10,
        shadowOffset: { width: 0, height: 6 },
        elevation: 3,
    },
    filterHead: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 10 },
    filterIcon: { fontSize: 16, color: "#F97316" },
    filterTitle: { fontSize: 14, fontWeight: "900", color: "#1F2937" },

    chip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999 },
    chipActive: { backgroundColor: "#F43F5E", shadowColor: "#000", shadowOpacity: 0.12, shadowRadius: 6, shadowOffset: { width: 0, height: 3 }, elevation: 2 },
    chipInactive: { backgroundColor: "rgba(244,63,94,0.10)" },
    chipText: { fontSize: 12, fontWeight: "900" },

    dateRow: { flexDirection: "row", gap: 12, marginTop: 8 },
    dateLabel: { fontSize: 11, color: "rgba(74,35,17,0.55)", fontWeight: "800", marginLeft: 2, marginBottom: 6 },
    dateBox: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        backgroundColor: "rgba(243,244,246,0.85)",
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.08)",
        borderRadius: 12,
        paddingHorizontal: 10,
        paddingVertical: 10,
    },
    dateBoxIcon: { fontSize: 14, opacity: 0.6 },
    dateBoxText: { fontSize: 13, fontWeight: "800", color: "rgba(31,41,55,0.90)" },

    emptyBox: {
        backgroundColor: "rgba(255,255,255,0.55)",
        borderRadius: 16,
        paddingVertical: 28,
        alignItems: "center",
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
    },
    emptyText: { marginTop: 8, color: "rgba(74,35,17,0.55)", fontWeight: "800" },

    itemCard: {
        backgroundColor: "rgba(255,255,255,0.95)",
        borderRadius: 16,
        padding: 14,
        borderWidth: 1,
        borderColor: "rgba(244,63,94,0.10)",
        shadowColor: "#000",
        shadowOpacity: 0.06,
        shadowRadius: 10,
        shadowOffset: { width: 0, height: 5 },
        elevation: 2,
    },
    itemTop: {
        flexDirection: "row",
        justifyContent: "space-between",
        paddingBottom: 10,
        borderBottomWidth: 1,
        borderBottomColor: "rgba(0,0,0,0.04)",
    },
    itemDate: { fontSize: 14, fontWeight: "900", color: "#4A2311" },
    itemDay: { fontSize: 11, marginTop: 4, color: "rgba(74,35,17,0.45)", fontWeight: "800" },

    itemMarks: { fontSize: 18, fontWeight: "900", color: "#E11D48" },
    itemMarksLabel: { fontSize: 10, marginTop: 2, color: "rgba(0,0,0,0.35)", fontWeight: "900", textTransform: "uppercase" },

    itemBottom: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginTop: 12 },
    statusRow: { flexDirection: "row", gap: 18 },
    statusCol: { alignItems: "center", gap: 4 },
    statusText: { fontSize: 10, color: "rgba(0,0,0,0.45)", fontWeight: "800" },

    dot: { width: 22, height: 22, borderRadius: 999, alignItems: "center", justifyContent: "center" },
    dotActive: { backgroundColor: "rgba(34,197,94,0.12)" },
    dotInactive: { backgroundColor: "rgba(0,0,0,0.06)" },

    actionRow: { flexDirection: "row", gap: 10 },
    iconBtn: {
        width: 36,
        height: 36,
        borderRadius: 10,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(244,63,94,0.10)",
    },
    iconBtnDanger: { backgroundColor: "rgba(220,38,38,0.10)" },
    iconBtnPlaceholder: { opacity: 0 },
    iconBtnText: { fontSize: 14, fontWeight: "900", color: "#E11D48" },

    modalOverlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.35)",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
    },
    modalCard: {
        width: "100%",
        maxWidth: 380,
        backgroundColor: "#fff",
        borderRadius: 18,
        padding: 16,
        shadowColor: "#000",
        shadowOpacity: 0.18,
        shadowRadius: 18,
        shadowOffset: { width: 0, height: 10 },
        elevation: 6,
    },
    modalHead: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
    modalTitle: { fontSize: 16, fontWeight: "900", color: "#111827" },
    modalClose: { width: 34, height: 34, borderRadius: 999, alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0,0,0,0.06)" },
    modalDate: { marginTop: 10, fontSize: 12, color: "rgba(0,0,0,0.55)", fontWeight: "700" },

    editOpt: {
        padding: 12,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.10)",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },
    editOptActive: { backgroundColor: "rgba(244,63,94,0.10)", borderColor: "rgba(244,63,94,0.30)" },
    editOptText: { fontSize: 13, fontWeight: "800", color: "#111827" },
    editOptTick: { fontSize: 16, fontWeight: "900", color: "#E11D48" },

    modalBtns: { flexDirection: "row", gap: 10, marginTop: 16 },
    modalCancel: {
        flex: 1,
        height: 46,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.12)",
        backgroundColor: "rgba(255,255,255,0.95)",
        alignItems: "center",
        justifyContent: "center",
    },
    modalCancelText: { fontSize: 14, fontWeight: "900", color: "rgba(74,35,17,0.60)" },
});
